<template lang="pug">
.table-wrapper(ref="wrapperRef", @click="clearHighlight")
  slot
</template>

<script>
import {
  defineComponent,
  ref,
  onMounted,
  onUpdated,
  onBeforeUnmount,
  watch
} from 'vue'
import { useHighlightRow } from '@/composables/useHighlightRow'
import { cloneDeep, isNumber } from 'lodash'
export default defineComponent({
  name: 'HighlightWrapper',
  props: {
    items: {
      type: Array,
      default: () => []
    },
    currentPage: {
      type: Number || String,
      default: 1
    },
    currentPrimaryKey: {
      type: String,
      default: 'id'
    },
    isOpenDialog: {
      type: Boolean,
      default: false
    },
    lastIndexCurrentRow: {
      type: Number,
      required: false
    },
    searchLastPage: {
      type: Function,
      required: false
    },
    totalPage: {
      type: Number,
      default: 1
    },
    handleClickFunc: {
      type: Function,
      required: false
    },
    isFocusMode: {
      type: Boolean,
      default: false
    },
    lastIndexCurrentRowFocus: {
      type: Number,
      required: false
    },
    searchLastPageFocus: {
      type: Function,
      required: false
    },
    handleFocusFunc: {
      type: Function,
      required: false
    },
    triggerFunc: {
      type: Function,
      required: false
    },
    tableName: {
      type: String,
      required: false
    },
    tableClass: {
      type: String,
      default: 'common-table-container'
    }
  },
  setup(props) {
    const wrapperRef = ref(null)
    const {
      // Reactive state
      currentPage: currentPageHook,
      currentPosition,
      currentPrimaryKey: currentPrimaryKeyHook,
      currentItemList,

      // Methods
      setCurrentPage,
      setCurrentPosition,
      setCurrentPrimaryKey,
      setCurrentItemList,
      clearAll,
      remember,
      reSelect
    } = useHighlightRow()

    const {
      currentPage: currentPageFocus,
      currentPosition: currentPositionFocus,
      currentPrimaryKey: currentPrimaryKeyFocus,
      currentItemList: currentItemListFocus,
      setCurrentPage: setCurrentPageFocus,
      setCurrentPosition: setCurrentPositionFocus,
      setCurrentPrimaryKey: setCurrentPrimaryKeyFocus,
      setCurrentItemList: setCurrentItemListFocus,
      clearAll: clearAllFocus,
      remember: rememberFocus,
      reSelect: reSelectFocus
    } = useHighlightRow()
    const rowIndex = ref(null)
    watch(
      () => props.tableClass,
      (newVal) => {
        if (!newVal) return
        const styleId = 'highlight-wrapper-hover-style'
        let style = document.getElementById(styleId)
        if (!style) {
          style = document.createElement('style')
          style.id = styleId
          document.head.appendChild(style)
        }
        style.innerHTML = `.${newVal} table tbody tr:hover { background-color: white !important; }`
      },
      { immediate: true }
    )

    watch(
      () => props.lastIndexCurrentRow,
      (newVal) => {
        rowIndex.value = newVal
      }
    )
    const highlightRow = (rowIndex) => {
      if (!wrapperRef.value) return
      // Clear previous highlight first
      wrapperRef.value
        .querySelectorAll('tr.is-active')
        .forEach((el) => el.classList.remove('is-active'))

      const rows = wrapperRef.value.querySelectorAll('tbody tr')
      const row = rows[rowIndex]

      if (row) {
        row.classList.add('is-active')
      }
    }

    const focusRow = (rowIndex) => {
      if (!wrapperRef.value || !props.isFocusMode) return
      // Clear previous focus first
      wrapperRef.value
        .querySelectorAll('tr.is-focus')
        .forEach((el) => el.classList.remove('is-focus'))

      const rows = wrapperRef.value.querySelectorAll('tbody tr')
      const row = rows[rowIndex]

      if (row) {
        row.classList.add('is-focus')
      }
    }

    const clearFocus = () => {
      if (!wrapperRef.value || !props.isFocusMode) return
      wrapperRef.value
        .querySelectorAll('tr.is-focus')
        .forEach((el) => el.classList.remove('is-focus'))
    }

    const clearHighlight = () => {
      if (!wrapperRef.value) return
      wrapperRef.value
        .querySelectorAll('tr.is-active')
        .forEach((el) => el.classList.remove('is-active'))
      clearFocus()
    }

    const handleKeyDown = (e) => {
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown' && e.key !== 'Enter')
        return

      // If controlled by parent, just clear the highlight
      if (
        props.handleClickFunc &&
        (e.key == 'ArrowUp' || e.key == 'ArrowDown')
      ) {
        clearHighlight()
      }
      if (props.isFocusMode && props.handleFocusFunc && e.key !== 'Enter') {
        clearFocus()
      }

      if (
        props.handleClickFunc ||
        (props.isFocusMode && props.handleFocusFunc)
      ) {
        return
      }

      // Uncontrolled: handle navigation internally
      e.preventDefault() // Prevent page scrolling
      if (!wrapperRef.value) return

      const rows = wrapperRef.value.querySelectorAll('tbody tr')
      const rowCount = rows.length
      if (rowCount === 0) return

      let newIndex = rowIndex.value
      if (e.key === 'ArrowDown') {
        newIndex++
      } else if (e.key === 'ArrowUp') {
        newIndex--
      }

      // Clamp index to be within bounds [0, rowCount - 1]
      newIndex = Math.max(0, Math.min(newIndex, rowCount - 1))

      highlightRow(newIndex)
      if (props.isFocusMode) {
        let newIndexFocus = props.lastIndexCurrentRowFocus
        if (e.key === 'ArrowDown') {
          newIndexFocus++
        } else if (e.key === 'ArrowUp') {
          newIndexFocus--
        }
        newIndexFocus = Math.max(0, Math.min(newIndexFocus, rowCount - 1))
        focusRow(newIndexFocus)
      }
    }

    const handleClick = (event) => {
      if (!props.isOpenDialog) {
        const elements = document.querySelectorAll('.' + props.tableClass)
        let isClickOutsideAll = true
        elements.forEach((element) => {
          if (element.contains(event.target)) {
            isClickOutsideAll = false
          }
        })

        if (isClickOutsideAll) {
          rowIndex.value = null
        }
      }
    }

    onMounted(() => {
      document.addEventListener('click', handleClick)
      window.addEventListener('keydown', handleKeyDown)
    })

    onBeforeUnmount(() => {
      window.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('click', handleClick)

      const style = document.getElementById('highlight-wrapper-hover-style')
      if (style) {
        style.remove()
      }
    })
    /**
     * Remembers the currently highlighted row, executes a callback to potentially
     * refresh the data, and then restores the focus to the correct row.
     * This is useful for maintaining user context after data reloads.
     */
    const handleHoldHighlight = async () => {
      const position = await remember({
        new_item_list: cloneDeep(props.items),
        primary_key_name: 'id',
        last_page: props.totalPage,
        callback: props.searchLastPage
      })
      if (isNumber(position)) {
        if (props.handleClickFunc) {
          if (props.triggerFunc) {
            props.triggerFunc(props.tableName)
          }
          props.handleClickFunc(position)
        } else highlightRow(position)
      }

      clearAll()
    }

    const handleHoldFocus = async () => {
      if (!props.isFocusMode) return
      const position = await rememberFocus({
        new_item_list: cloneDeep(props.items),
        primary_key_name: 'id',
        last_page: props.totalPage,
        callback: props.searchLastPageFocus
      })
      if (isNumber(position)) {
        if (props.handleFocusFunc) {
          if (props.triggerFunc) {
            props.triggerFunc(props.tableName)
          }
          props.handleFocusFunc(position)
        } else focusRow(position)
      }

      clearAllFocus()
    }
    watch(
      () => props.isOpenDialog,
      (newVal) => {
        if (newVal) {
          reSelect({
            current_page: cloneDeep(props.currentPage),
            current_position: cloneDeep(rowIndex.value),
            current_primary_key: 'id',
            current_item_list: cloneDeep(props.items)
          })
          highlightRow(props.lastIndexCurrentRow)
          if (props.isFocusMode) {
            reSelectFocus({
              current_page: cloneDeep(props.currentPage),
              current_position: cloneDeep(props.lastIndexCurrentRowFocus),
              current_primary_key: 'id',
              current_item_list: cloneDeep(props.items)
            })
            focusRow(props.lastIndexCurrentRowFocus)
          }
        } else {
          handleHoldHighlight()
          handleHoldFocus()
        }
      }
    )

    onUpdated(() => {
      if (props.isOpenDialog) {
        if (rowIndex.value !== null && rowIndex.value !== undefined) {
          highlightRow(rowIndex.value)
        }
        if (
          props.isFocusMode &&
          props.lastIndexCurrentRowFocus !== null &&
          props.lastIndexCurrentRowFocus !== undefined
        ) {
          focusRow(props.lastIndexCurrentRowFocus)
        }
      }
    })

    return {
      wrapperRef,
      highlightRow,
      clearHighlight,
      focusRow,
      clearFocus
    }
  }
})
</script>
